<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import CampaignHistoryRow from 'dashboard/components-next/Marketing/CampaignHistoryRow.vue';
import BroadcastPreviewDialog from 'dashboard/components-next/Marketing/Broadcasts/BroadcastPreviewDialog.vue';
import DeleteCampaignDialog from 'dashboard/components-next/Marketing/DeleteCampaignDialog.vue';

const { t } = useI18n();
const store = useStore();

const previewDialogRef = ref(null);
const activePreview = ref(null);
const deleteDialogRef = ref(null);
const campaignToDelete = ref(null);
const isDeleting = ref(false);

const broadcastFlags = useMapGetter('broadcasts/getUIFlags');
const postFlags = useMapGetter('socialPosts/getUIFlags');
const broadcasts = useMapGetter('broadcasts/getBroadcasts');
const posts = useMapGetter('socialPosts/getSocialPosts');

const isFetching = computed(
  () => broadcastFlags.value.isFetching || postFlags.value.isFetching
);
const isPreviewing = computed(() => broadcastFlags.value.isPreviewing);

// A campaign fans out into a post and a broadcast that share a campaign_uid.
// Records created before that column existed have none, so they stand alone.
const campaigns = computed(() => {
  const grouped = new Map();

  const upsert = (key, record, slot) => {
    const entry = grouped.get(key) ?? { key, post: null, broadcast: null };
    entry[slot] = record;
    entry.createdAt = Math.max(
      entry.createdAt ?? 0,
      new Date(record.created_at).getTime() || 0
    );
    grouped.set(key, entry);
  };

  (posts.value ?? []).forEach(post =>
    upsert(post.campaign_uid || `post-${post.id}`, post, 'post')
  );
  (broadcasts.value ?? []).forEach(broadcast =>
    upsert(
      broadcast.campaign_uid || `broadcast-${broadcast.id}`,
      broadcast,
      'broadcast'
    )
  );

  return [...grouped.values()].sort((a, b) => b.createdAt - a.createdAt);
});

const isEmpty = computed(
  () => campaigns.value.length === 0 && !isFetching.value
);

const handlePreview = async broadcast => {
  activePreview.value = null;
  previewDialogRef.value.dialogRef.open();
  try {
    activePreview.value = await store.dispatch(
      'broadcasts/preview',
      broadcast.id
    );
  } catch (error) {
    useAlert(t('MARKETING.BROADCASTS.API.PREVIEW_ERROR'));
  }
};

const handleTrigger = async broadcast => {
  try {
    await store.dispatch('broadcasts/trigger', broadcast.id);
    useAlert(t('MARKETING.BROADCASTS.API.TRIGGER_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('MARKETING.BROADCASTS.API.TRIGGER_ERROR')
    );
  }
};

const handlePublish = async post => {
  try {
    await store.dispatch('socialPosts/publish', post.id);
    useAlert(t('MARKETING.POSTS.API.PUBLISH_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('MARKETING.POSTS.API.PUBLISH_ERROR')
    );
  }
};

// Antes borraba sin preguntar y solo de Ochat. Ahora pregunta y ofrece borrar
// también de las redes: ver DeleteCampaignDialog.
const handleDelete = campaign => {
  campaignToDelete.value = campaign;
  deleteDialogRef.value.open();
};

// Lo que quedó publicado porque la API no deja borrarlo, para que el operador
// sepa que tiene que hacerlo a mano.
const STILL_PUBLISHED = ['manual', 'needs_permission'];
const deletionSummary = results => {
  const pending = results.filter(result =>
    STILL_PUBLISHED.includes(result.outcome)
  );
  if (!pending.length) return t('MARKETING.HISTORY.DELETE_SUCCESS');

  return t('MARKETING.HISTORY.DELETE_DIALOG.STILL_PUBLISHED', {
    targets: pending.map(result => result.inbox_name).join(', '),
  });
};

// Primero el posteo, que puede fallar al borrar de la red: si falla, la difusión
// tampoco se toca y la campaña queda entera para reintentar.
const confirmDelete = async ({ removeFromNetworks }) => {
  const campaign = campaignToDelete.value;
  isDeleting.value = true;
  try {
    let results = [];
    if (campaign.post) {
      results = await store.dispatch('socialPosts/delete', {
        id: campaign.post.id,
        removeFromNetworks,
      });
    }
    if (campaign.broadcast) {
      await store.dispatch('broadcasts/delete', campaign.broadcast.id);
    }
    deleteDialogRef.value.close();
    useAlert(deletionSummary(results));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('MARKETING.HISTORY.DELETE_ERROR')
    );
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-4 md:px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center w-full h-20">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('MARKETING.HISTORY.HEADER_TITLE') }}
          </span>
        </div>
      </div>
    </header>
    <main class="flex-1 px-4 md:px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <EmptyStateLayout
          v-else-if="isEmpty"
          :title="t('MARKETING.HISTORY.EMPTY_STATE.TITLE')"
          :subtitle="t('MARKETING.HISTORY.EMPTY_STATE.SUBTITLE')"
          class="pt-14"
        />
        <div v-else class="flex flex-col gap-3">
          <CampaignHistoryRow
            v-for="campaign in campaigns"
            :key="campaign.key"
            :campaign="campaign"
            @preview="handlePreview"
            @trigger="handleTrigger"
            @publish="handlePublish"
            @delete="handleDelete"
          />
        </div>

        <BroadcastPreviewDialog
          ref="previewDialogRef"
          :preview="activePreview"
          :is-loading="isPreviewing"
        />
        <DeleteCampaignDialog
          ref="deleteDialogRef"
          :campaign="campaignToDelete"
          :is-loading="isDeleting"
          @confirm="confirmDelete"
        />
      </div>
    </main>
  </section>
</template>
